/**
 * SourcesFooter — the citation section rendered at the bottom of an
 * assistant answer.
 *
 * Collapsible "Sources · N" disclosure (collapsed by default) that
 * expands to a numbered list of the cited sources. Shared by the legacy
 * `AssistantMessage` and the V3 `RenderedAssistantMessage` so every
 * source type (document / timeseries / pid_diagram / knowledge_graph)
 * gets the identical UI — closing the gap where P&ID (kg-agent) answers
 * showed no citation while timeseries/document answers did.
 *
 * Consumes camelCase `SourcePart[]`. The kg-agent `data-kg-sources`
 * snapshot reaches this as `source` parts via `buildV2StreamParts`
 * (live) and `Message.sources` hydration (reload). Rows are de-duped by
 * (citationType:id) so the `N` count and the list are honest when one
 * document is cited by several chunks.
 *
 * Uses a native <details>/<summary> for the disclosure so keyboard /
 * screen-reader behavior is free (same pattern as ReasoningRenderer).
 *
 * ENG-2678 — the source title IS the Data Catalog filename (the backend
 * resolver `native-agent/citations/resolve-citations.ts` is the one place
 * that decides it, and deliberately renders the document's real name
 * rather than the internal `R279032010P02` sheet key). The answer's prose
 * names the drawing the way the title block does (`RA-279032-010-P02`),
 * so this footer is where a reader gets the string that actually matches
 * the Catalog. Three things make it obtainable rather than merely
 * present: the first source's name rides in the summary line (visible
 * with the disclosure still closed, so no answer grows taller), every
 * name carries a `title` tooltip because the row truncates, and a text
 * selection no longer triggers the row's open-in-new-tab so the name can
 * be copied and pasted into search.
 */
import { File, FileText, GitFork, Network, TableProperties, ExternalLink, ChevronRight } from 'lucide-react'
import { useTranslation } from '@/i18n'
import type { SourcePart } from '../../types'

const CITATION_ICON_BY_TYPE = {
  document: File,
  timeseries: TableProperties,
  pid_diagram: GitFork,
  knowledge_graph: Network,
} as const

/** Stable de-dup key so a source cited by multiple chunks renders once. */
function sourceKey(s: SourcePart): string {
  return `${s.citationType ?? 'document'}:${s.docId || s.tagId || s.entityId || s.sourceId}`
}

type TFunc = (key: string, opts: Record<string, unknown>) => string

/** True when the user currently has text selected — i.e. this click was a copy, not a nav. */
function isTextSelected(): boolean {
  return (window.getSelection()?.toString().trim().length ?? 0) > 0
}

function citationTitle(source: SourcePart, index: number, t: TFunc): string {
  return (
    source.title ||
    (source.citationType === 'document' && source.docId
      ? t('chatv2:sources.title_document', { id: source.docId, defaultValue: 'Document: {{id}}' })
      : source.citationType === 'timeseries' && source.tagId
        ? t('chatv2:sources.title_tag', { id: source.tagId, defaultValue: 'Tag: {{id}}' })
        : source.citationType === 'pid_diagram' && source.entityId
          ? t('chatv2:sources.title_pid', { id: source.entityId, defaultValue: 'P&ID: {{id}}' })
          : source.citationType === 'knowledge_graph' && source.entityId
            ? t('chatv2:sources.title_entity', { id: source.entityId, defaultValue: 'Entity: {{id}}' })
            : t('chatv2:sources.title_index', { number: index + 1, defaultValue: 'Source {{number}}' }))
  )
}

function citationSubtitle(source: SourcePart, t: TFunc): string | undefined {
  if (source.citationType === 'document' && source.pageNumber) return t('chatv2:sources.page', { number: source.pageNumber, defaultValue: 'Page {{number}}' })
  if (source.citationType === 'timeseries' && source.timeRangeStart && source.timeRangeEnd) {
    return `${source.timeRangeStart} - ${source.timeRangeEnd}`
  }
  if (source.pidSource) return source.pidSource
  if (source.content) return source.content.slice(0, 60) + (source.content.length > 60 ? '...' : '')
  return undefined
}

export function SourcesFooter({ sourceParts }: { sourceParts: SourcePart[] }) {
  const { t } = useTranslation()
  // De-dupe by (type:id), preserving first-seen order, so the count and
  // the list match (one document cited by N chunks = one source).
  const seen = new Set<string>()
  const unique: SourcePart[] = []
  for (const s of sourceParts) {
    if (!s || typeof s !== 'object') continue
    const key = sourceKey(s)
    if (seen.has(key)) continue
    seen.add(key)
    unique.push(s)
  }
  if (unique.length === 0) return null

  const label = `${unique.length === 1
    ? t('chatv2:sources.label_one', { defaultValue: 'Source' })
    : t('chatv2:sources.label_other', { defaultValue: 'Sources' })} · ${unique.length}`
  // ENG-2678: the leading source's catalog filename, shown in the closed summary so the
  // searchable string is readable without expanding (and without making answers taller).
  const leadTitle = citationTitle(unique[0], 0, t)

  return (
    <details
      data-testid="sources-footer"
      className="group mt-4 pt-3 border-t border-gray-100 dark:border-gray-800"
    >
      <summary
        data-testid="sources-summary"
        className="flex cursor-pointer list-none items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 [&::-webkit-details-marker]:hidden"
      >
        <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500 transition-transform group-open:rotate-90" />
        <span className="flex-shrink-0">{label}</span>
        <span
          data-testid="sources-summary-lead"
          title={leadTitle}
          className="min-w-0 truncate font-normal text-gray-500 dark:text-dark-text-muted group-open:hidden"
        >
          {leadTitle}
        </span>
      </summary>
      <ol className="mt-2 flex flex-col gap-1.5 list-none m-0 p-0">
        {unique.map((source, index) => {
          const Icon = CITATION_ICON_BY_TYPE[source.citationType || 'document'] || FileText
          const title = citationTitle(source, index, t)
          const subtitle = citationSubtitle(source, t)

          return (
            <li key={source.sourceId || index}>
              <div
                className="flex items-start gap-2 p-2 bg-gray-50 dark:bg-dark-muted hover:bg-gray-100 dark:hover:bg-dark-accent rounded-lg border border-gray-200 dark:border-dark-border cursor-pointer transition-colors"
                onClick={() => {
                  // ENG-2678: selecting the filename to copy it ends in a click on this
                  // row; opening a tab then throws away the selection the user just made.
                  if (!isTextSelected() && source.url) {
                    window.open(source.url, '_blank')
                  }
                }}
              >
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-medium flex items-center justify-center">
                  {index + 1}
                </span>

                <Icon className="flex-shrink-0 h-4 w-4 text-gray-500 dark:text-dark-text-muted mt-0.5" />

                <div className="flex-1 min-w-0">
                  <div
                    title={title}
                    className="select-text text-sm font-medium text-gray-700 dark:text-gray-300 truncate"
                  >
                    {title}
                  </div>
                  {subtitle && (
                    <div className="text-xs text-gray-500 dark:text-dark-text-muted truncate">
                      {subtitle}
                    </div>
                  )}
                </div>

                {source.confidence !== undefined && source.confidence > 0 && (
                  <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
                    {Math.round(source.confidence * 100)}%
                  </span>
                )}

                {source.url && (
                  <ExternalLink className="flex-shrink-0 h-3 w-3 text-gray-400 dark:text-gray-500" />
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </details>
  )
}
